import { useAdminReports, useAdminReport } from 'data/hooks'
import { AdminReports_adminReports as TAdminReport } from 'data/queries/types/AdminReports'
import { ReportReportType } from 'data/types/graphql-global-types'
import { useEffect, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FlexRowWithCTA, RenderResponse, EmptyState } from 'ui/components'
import { useAlerts, AlertVariants } from 'ui/components/Alerts'
import { useDownloadInBrowser } from 'ui/hooks'

// Report types whose files are xlsx workbooks rather than CSVs.
const XLSX_REPORT_TYPES = new Set<ReportReportType>([ReportReportType.EOM_ADJUSTMENTS])

const getFileExtension = (reportType: ReportReportType | null): string =>
  reportType !== null && XLSX_REPORT_TYPES.has(reportType) ? '.xlsx' : '.csv'

const AdminReports = () => {
  const { t } = useTranslation()
  const { addAlert } = useAlerts()
  const { loading, error, data } = useAdminReports()
  const {
    getReport,
    reset,
    loading: reportLoading,
    error: reportError,
    data: report,
  } = useAdminReport()

  const [downloadingHashId, setDownloadingHashId] = useState<string | null>(null)
  const [downloadingReportType, setDownloadingReportType] =
    useState<ReportReportType | null>(null)

  const reports = data?.adminReports || []
  const hasReports = reports && !!reports.length
  const reportURL = report?.url
  const reportName = report?.name
  const reportFilename = reportName || 'report'
  const filenameWithExt = `${reportFilename}${getFileExtension(downloadingReportType)}`

  useDownloadInBrowser({ url: reportURL, error: reportError, filename: filenameWithExt })

  const addErrorAlert = useCallback(() => {
    addAlert({
      variant: AlertVariants.error,
      message: t('admin_documents.download_error', { name: filenameWithExt }),
      autoClose: 8000,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportError])

  const handleDownloadClick = (
    hashId: string,
    name: string,
    reportType: ReportReportType | null,
  ) => {
    setDownloadingHashId(hashId)
    setDownloadingReportType(reportType)
    getReport(hashId, name)
      .catch(addErrorAlert)
      .finally(() => {
        setTimeout(() => {
          reset()
          setDownloadingHashId(null)
          setDownloadingReportType(null)
        }, 0)
      })
  }

  useEffect(() => {
    if (reportError) {
      addErrorAlert()
    }
  }, [reportError, addErrorAlert])

  return (
    <div data-testid="admin-reports">
      <RenderResponse {...{ loading, error }}>
        {hasReports ? (
          <>
            {reports.map(({ hashId, filename, reportType }: TAdminReport) => (
              <FlexRowWithCTA
                key={hashId}
                id={hashId}
                name={`${filename}${getFileExtension(reportType)}`}
                handleClick={() => handleDownloadClick(hashId, filename, reportType)}
                isDisabled={reportLoading}
                isLoading={reportLoading && downloadingHashId === hashId}
                ctaText={t('admin_documents.download')}
              />
            ))}
          </>
        ) : (
          <EmptyState message={t('admin_documents.no_reports')} />
        )}
      </RenderResponse>
    </div>
  )
}

export { AdminReports }
